// src/pages/Curadoria.tsx

import React, { useState, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useAudios } from '../hooks/useAudios';
import { AudioWithUrl } from '../types/audio.types';
import { getSignedUrlCached } from '../services/signedUrlService';
import AudioFilters from '../components/AudioFilters';
import AudioPlayer from '../components/AudioPlayer';
import LabelingPanel from '../components/LabelingPanel';
import AudioList from '../components/AudioList';
import WaveformComparison from '../components/WaveformComparison';

type ViewMode = 'labeling' | 'waveform';

const Curadoria: React.FC = () => {
  const { userProfile, logout, canLabel, currentUser } = useAuth();
  const {
    audios,
    loading,
    error,
    filters,
    selectedAudio,
    updateFilters,
    clearFilters,
    selectAudio
  } = useAudios();

  const [showFilters, setShowFilters] = useState(true);
  const [viewMode, setViewMode] = useState<ViewMode>('labeling');
  const [selectedAudiosForWaveform, setSelectedAudiosForWaveform] = useState<string[]>([]);
  const [loadedAudiosWithUrls, setLoadedAudiosWithUrls] = useState<Map<string, AudioWithUrl>>(new Map());
  const [loadingAudioIds, setLoadingAudioIds] = useState<Set<string>>(new Set());

  // Estatísticas calculadas dos áudios
  const stats = useMemo(() => {
    const total = audios.length;
    const labeled = audios.filter(a => a.current_label).length;
    const withMurmur = audios.filter(a => a.murmur === 'Present').length;
    const abnormal = audios.filter(a => a.outcome === 'Abnormal').length;
    const normal = audios.filter(a => a.outcome === 'Normal').length;
    
    // Contagem por foco
    const byFocus: Record<string, number> = {};
    audios.forEach(a => {
      byFocus[a.focus] = (byFocus[a.focus] || 0) + 1;
    });

    // Contagem por sexo
    const bySex: Record<string, number> = {};
    audios.forEach(a => {
      bySex[a.biological_sex] = (bySex[a.biological_sex] || 0) + 1;
    });

    return {
      total,
      labeled,
      unlabeled: total - labeled,
      withMurmur,
      abnormal,
      normal,
      byFocus,
      bySex
    };
  }, [audios]);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
    }
  };

  const handleToggleAudioForWaveform = async (audioId: string) => {
    const isCurrentlySelected = selectedAudiosForWaveform.includes(audioId);
    
    if (isCurrentlySelected) {
      setSelectedAudiosForWaveform(prev => prev.filter(id => id !== audioId));
      setLoadedAudiosWithUrls(prev => {
        const newMap = new Map(prev);
        newMap.delete(audioId);
        return newMap;
      });
      setLoadingAudioIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(audioId);
        return newSet;
      });
    } else {
      if (selectedAudiosForWaveform.length >= 10) {
        alert('⚠️ Máximo de 6 áudios para comparação simultânea.\n\nRemova um áudio antes de adicionar outro.');
        return;
      }
      
      if (!currentUser) {
        alert('Erro: Usuário não autenticado. Faça login novamente.');
        return;
      }
      
      setLoadingAudioIds(prev => new Set(prev).add(audioId));
      
      try {
        const audioMetadata = audios.find(a => a.audioId === audioId);
        if (!audioMetadata) {
          throw new Error('Áudio não encontrado na lista');
        }

        const signedUrl = await getSignedUrlCached(
          audioId,
          currentUser.uid,
          currentUser.email || null
        );

        const audioWithUrl: AudioWithUrl = {
          ...audioMetadata,
          signedUrl,
          signedUrlExpiry: new Date(Date.now() + 3600000)
        };

        setSelectedAudiosForWaveform(prev => [...prev, audioId]);
        setLoadedAudiosWithUrls(prev => new Map(prev).set(audioId, audioWithUrl));
        
      } catch (err) {
        console.error('Erro ao carregar áudio:', err);
        alert('Erro ao carregar áudio para comparação. Tente novamente.');
      } finally {
        setLoadingAudioIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(audioId);
          return newSet;
        });
      }
    }
  };

  const clearWaveformSelection = () => {
    setSelectedAudiosForWaveform([]);
    setLoadedAudiosWithUrls(new Map());
    setLoadingAudioIds(new Set());
  };

  const selectedAudiosForComparison = selectedAudiosForWaveform
    .map(id => loadedAudiosWithUrls.get(id))
    .filter((audio): audio is AudioWithUrl => audio !== undefined && audio.signedUrl !== undefined);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <header className="bg-white shadow-md border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <div className="w-12 h-11 flex items-center justify-center">
                {/* Logo SHE SVG */}
                <svg 
                  className="w-full h-full" 
                  xmlns="http://www.w3.org/2000/svg" 
                  viewBox="0 0 453.9 428.6"
                >
                  <g>
                    <path 
                      fill="#8e162e" 
                      d="M267,67.3c2.5,3.2,5.8,5.7,9.5,7.1,31.4,12.3,57.6,35,74.5,63.7,1.6,2.7,4.4,4.4,7.5,4.4h0c6.7,0,10.9-7.3,7.5-13.1-22.8-38.6-60.5-67.3-105.2-78.1-2.6-.6-4.5,2.4-2.9,4.4l9,11.5Z"
                    />
                    <path 
                      fill="#8e162e" 
                      d="M176.3,360.6c-2.1-2.7-4.9-4.6-8.1-5.9-29.6-11.3-54.8-31.7-72-57.8-1.6-2.5-4.4-4-7.3-4h0c-6.9,0-11.1,7.6-7.3,13.4,23.2,35.2,59.3,61.2,101.6,71.2s4.5-2.4,2.9-4.5l-9.7-12.4Z"
                    />
                    <path 
                      fill="#8e162e" 
                      d="M347,296.9c-17.7,26.8-43.7,47.6-74.4,58.7-1.1.4-2,1.1-2.7,2l-11.9,15.2c-1.6,2.1.3,5.1,2.9,4.4,41.9-10.1,77.7-36,100.8-71,3.8-5.7-.5-13.4-7.3-13.4h0c-3,0-5.7,1.5-7.3,4Z"
                    />
                    <path 
                      fill="#8e162e" 
                      d="M92.2,138.1c17.1-28.9,43.6-51.7,75.3-64,3.8-1.5,7.1-4,9.6-7.2l8.9-11.4c1.6-2.1-.3-5.1-2.9-4.5-45.1,10.6-83.1,39.5-106,78.3-3.4,5.8.8,13.1,7.5,13.1h0c3.1,0,5.9-1.7,7.5-4.4Z"
                    />
                  </g>
                  <g>
                    <path 
                      fill="#bd274a" 
                      d="M223.8,178.9l74.9-48.2-50-64.1-15.1-19.4-1.3-1.7c-2.6-3.3-6.4-5-10.3-5-3.8,0-7.6,1.7-10.2,5l-1.3,1.6-15.1,19.3-50.7,64.7,74.3,47.8c1.5,1,3.4,1,4.9,0Z"
                    />
                    <path 
                      fill="#bd274a" 
                      d="M417.7,212.1l-31.7-30.9-20.3-19.8c-1.2-1.2-2.7-1.9-4.3-2.1-.4,0-.7-.1-1.1-.1-2.1,0-4.2.9-5.8,2.6l-16.8,19-32.1-41.1-69.7,60c-1.3,1.1-2.2,2.6-2.3,4.3l-8.5,109.6c-.1,1.8-1.7,3.3-3.5,3.3s-3.4-1.4-3.5-3.3l-9.1-109.7c-.1-1.7-1-3.3-2.3-4.3l-69.1-59.6-32,40.8-16.9-19.1c-1.5-1.7-3.6-2.6-5.8-2.6-.4,0-.8,0-1.1.1-1.5.2-3,.9-4.2,2.1l-20.3,19.8-31.7,30.9c-3.1,3-3.1,8,0,11l33.6,32.7,18.4,18c1.5,1.5,3.4,2.2,5.4,2.2s1.1,0,1.6-.2c1.6-.3,3-1.1,4.2-2.4l19.6-22.2,102.1,130.3,1.3,1.6c2.6,3.3,6.4,5,10.2,5s7.7-1.7,10.3-5l1.3-1.7,101.5-130.2,19.5,22c1.1,1.3,2.6,2.1,4.1,2.4.5.1,1.1.2,1.6.2,1.9,0,3.9-.7,5.4-2.2l18.4-17.9,33.6-32.8c3.1-3,3.1-8,0-11Z"
                    />
                  </g>
                </svg>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Sistema de Curadoria Clínica
                </h1>
                <p className="text-sm text-gray-600">
                  Rotulagem de áudios cardíacos • Dataset CirCor 1.0.3
                </p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              {userProfile && (
                <div className="text-right">
                  <p className="text-sm font-medium text-gray-900">
                    {userProfile.displayName || userProfile.email}
                  </p>
                  <div className="flex items-center justify-end gap-2">
                    <span className="text-xs px-2 py-0.5 bg-indigo-100 text-indigo-800 rounded-full font-medium">
                      {userProfile.role}
                    </span>
                    {canLabel() && (
                      <span className="text-xs px-2 py-0.5 bg-green-100 text-green-800 rounded-full font-medium">
                        Rotulagem ativa
                      </span>
                    )}
                  </div>
                </div>
              )}

              <button
                onClick={handleLogout}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition text-sm font-medium flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                Sair
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Stats Bar Principal */}
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Total</p>
                <p className="text-lg font-semibold text-gray-900">{stats.total}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Rotulados</p>
                <p className="text-lg font-semibold text-green-600">{stats.labeled}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Pendentes</p>
                <p className="text-lg font-semibold text-amber-600">{stats.unlabeled}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Murmúrios</p>
                <p className="text-lg font-semibold text-orange-600">{stats.withMurmur}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Anormais</p>
                <p className="text-lg font-semibold text-red-600">{stats.abnormal}</p>
              </div>
            </div>
          
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Normais</p>
                <p className="text-lg font-semibold text-green-600">{stats.normal}</p>
              </div>
            </div>
          </div>
        </div>


        {/* Mode Selector */}
        <div className="mb-6 bg-white rounded-lg shadow-sm border border-gray-200 p-5">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex-1">
              <h3 className="text-base font-semibold text-gray-900 mb-1 flex items-center gap-2">
                {viewMode === 'labeling' ? (
                  <>
                    <span className="w-2 h-2 bg-indigo-600 rounded-full"></span>
                    Modo Rotulagem Individual
                  </>
                ) : (
                  <>
                    <span className="w-2 h-2 bg-purple-600 rounded-full"></span>
                    Modo Análise Comparativa
                  </>
                )}
              </h3>
              <p className="text-sm text-gray-600">
                {viewMode === 'labeling'
                  ? 'Escute, analise e atribua rótulos clínicos aos áudios individualmente'
                  : `Compare visualmente as waveforms de múltiplos áudios (${selectedAudiosForWaveform.length}/10 selecionados)`
                }
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setViewMode('labeling');
                  clearWaveformSelection();
                }}
                className={`px-5 py-2.5 rounded-lg font-medium transition-all flex items-center gap-2 ${
                  viewMode === 'labeling'
                    ? 'bg-indigo-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                </svg>
                Rotulagem
              </button>
              <button
                onClick={() => setViewMode('waveform')}
                className={`px-5 py-2.5 rounded-lg font-medium transition-all flex items-center gap-2 ${
                  viewMode === 'waveform'
                    ? 'bg-purple-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
                </svg>
                Comparação
              </button>
            </div>
          </div>

          {viewMode === 'waveform' && selectedAudiosForWaveform.length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-700">
                  <span className="font-semibold text-purple-600">{selectedAudiosForWaveform.length}</span> áudio(s) selecionado(s) para comparação
                </p>
                <button
                  onClick={clearWaveformSelection}
                  className="text-sm text-red-600 hover:text-red-700 font-medium flex items-center gap-1 hover:underline"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  Limpar Seleção
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-lg shadow-sm">
            <div className="flex items-center gap-3">
              <svg
                className="w-6 h-6 text-red-600 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
              <div>
                <p className="font-medium text-red-800">Erro ao carregar dados</p>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Layout Principal */}
        {viewMode === 'labeling' ? (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-7 space-y-6">
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <svg className="w-5 h-5 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                    </svg>
                    Buscar Áudios
                  </h2>
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className="text-sm text-indigo-600 hover:text-indigo-700 font-medium flex items-center gap-1"
                  >
                    {showFilters ? (
                      <>
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                        </svg>
                        Ocultar Filtros
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                        Mostrar Filtros
                      </>
                    )}
                  </button>
                </div>

                {showFilters && (
                  <AudioFilters
                    filters={filters}
                    onFiltersChange={updateFilters}
                    onClear={clearFilters}
                  />
                )}
              </div>

              <AudioList
                audios={audios}
                selectedAudioId={selectedAudio?.audioId}
                onSelectAudio={(audio) => selectAudio(audio.audioId)}
                loading={loading}
              />
            </div>

            <div className="lg:col-span-5 space-y-6">
              <AudioPlayer audio={selectedAudio} />
              {canLabel() && selectedAudio && (
                <LabelingPanel audio={selectedAudio} canLabel={canLabel()} />
              )}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-4 space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <p className="text-sm font-semibold text-blue-900 mb-1">
                      Modo Comparação Ativo
                    </p>
                    <p className="text-xs text-blue-800">
                      Selecione até 10 áudios da lista para visualizar e comparar suas waveforms.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <svg className="w-5 h-5 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                    </svg>
                    Buscar Áudios
                  </h2>
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className="text-sm text-purple-600 hover:text-purple-700 font-medium flex items-center gap-1"
                  >
                    {showFilters ? 'Ocultar' : 'Mostrar'}
                  </button>
                </div>

                {showFilters && (
                  <AudioFilters
                    filters={filters}
                    onFiltersChange={updateFilters}
                    onClear={clearFilters}
                  />
                )}
              </div>

              {loading ? (
                <div className="bg-white rounded-lg shadow p-8 text-center">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-3 border-purple-600 border-t-transparent"></div>
                  <p className="mt-4 text-sm text-gray-600">Carregando áudios...</p>
                </div>
              ) : (
                <AudioList
                  audios={audios}
                  selectedAudioId={undefined}
                  onSelectAudio={() => {}}
                  loading={false}
                  selectionMode="multiple"
                  selectedAudioIds={selectedAudiosForWaveform}
                  onToggleSelection={handleToggleAudioForWaveform}
                  loadingAudioIds={loadingAudioIds}
                />
              )}
            </div>

            <div className="lg:col-span-8">
              <WaveformComparison
                audios={selectedAudiosForComparison}
                startTime={2}
                endTime={4}
              />
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default Curadoria;
